import { LocalDate } from "@js-joda/core";
import currency from "currency.js";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { dyn } from "trader.ts/ui";
import {
  constantField,
  dateBox,
  Field,
  Form,
  numberBox,
  selectBox,
  textBox,
} from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  invoiceid,
  periodic_payment_method,
  price_adapted_to_lg_and_student,
} from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import {
  makeArrayField,
  oneLineArrayRender,
  oneLineArrayRenderWithAlwaysAddAndDelete,
} from "../../client/arrayfield";
import { constantFieldWithRender } from "../../client/constantfieldwithrender";
import { currencyBox } from "../../client/financialfields";
import { renderCancelButton, renderSaveButton } from "../../client/savebutton";
import {
  non_periodic_payment_scheme,
  periodic_payment_scheme,
} from "../../domain/config";
import { invoiceFull } from "../../domain/finance/types";
import { absCurrency, negateCurrency } from "../../lib/currency";
import { mandatoryValidation } from "../../lib/validate";
import { parsePaymentMethod, paymentMethod } from "../../parsing_from_ur";
import { ddMMMMyyyy, renderCurrency } from "../../render";
import * as routes from "../../routes";
import { renderPaymentMethod } from "../../translations_specific";

export function render({
  invoice,
  payment_schemes,
}: {
  invoice: invoiceFull;
  payment_schemes: (non_periodic_payment_scheme | periodic_payment_scheme)[];
}): HTMLElement | HTMLElement[] {
  const payments = (invoice.payments || []).map((p) => ({
    amount: p.amount,
    date: p.date,
    method: parsePaymentMethod(p.method),
  }));

  const expectedPaymentsMemoryS = new Source(
    (invoice.expectedpayments || []).map((ep) => ({
      date: ep.date,
      amount: ep.amount,
      periodic_payment_method: ep.periodic_payment_method,
    })),
  );

  const form = new Form({})
    .addField("id", [], () => constantField(invoice.id))
    .addField("invoicelines", [], () =>
      invoicelinesField({ invoicelines: invoice.invoicelines || [] }),
    )
    .addField("creditnotes", [], () =>
      makeArrayField({
        initialVal: (invoice.creditnotes || [])
          .filter((cn) => cn.accountingsoftwareidentifier === null)
          .map((cn) => ({
            date: cn.date,
            amount: cn.amount,
            vat: cn.vat,
            externalid: cn.externalid as number | null,
          })),
        makeSubForm: async (cn) => {
          const ilForm = new Form({})
            .addField("date", [], () => dateBox({ initialVal: cn.date }))
            .addField("amount", [], () =>
              currencyBox(cn.amount, { constraint: "positive" }),
            )
            .addField("vat", [], () =>
              numberBox(cn.vat, { constraint: "positive" }),
            )
            .addField("externalid", [], () =>
              constantFieldWithRender(
                cn.externalid,
                <div
                  className={style.input_like}
                  style="filter: brightness(0.9);"
                >
                  {cn.externalid ? "#" + cn.externalid : ""}
                </div>,
              ),
            )
            .build({
              render: (fields) => [
                <div>{fields.externalid()}</div>,
                <div>{fields.amount()}</div>,
                <div className={style.equal_columns}>
                  {fields.vat()}
                  {fields.date()}
                </div>,
              ],
            });
          return ilForm;
        },
        makeNew: () => ({
          date: LocalDate.now(),
          amount: currency(0),
          vat: 0,
          externalid: null,
        }),
        render: oneLineArrayRender((il) => il.amount.value === 0),
      }),
    )
    .addField("payments", [], () =>
      makeArrayField({
        initialVal: payments
          .filter(
            (p) =>
              p.method !== "Online" &&
              p.method !== "AccountingSoftware" &&
              p.method !== "Chargeback" &&
              p.method !== "Refund" &&
              p.method !== "DirectDebit",
          )
          .filter((p) => p.amount.value >= 0),
        makeSubForm: async (paym) => {
          const ilForm = new Form({})
            .addField("date", [], () => dateBox({ initialVal: paym.date }))
            .addField("amount", [], () =>
              currencyBox(paym.amount, { constraint: "positive" }),
            )
            .addField("method", [], () => {
              const methods: paymentMethod[] = [
                "Cash",
                "Bancontact",
                "VISA",
                "Mastercard",
                "Wire",
                "Giftcard",
              ];
              return selectBox({
                values: methods,
                initial: paym.method,
                show: renderPaymentMethod,
              });
            })
            .build();
          return ilForm;
        },
        makeNew: () => ({
          date: LocalDate.now(),
          amount: currency(0),
          method: "Cash" as const,
        }),
        render: oneLineArrayRender((il) => il.amount.value === 0),
      }),
    )
    .addField("reimbursements", [], () =>
      makeArrayField({
        initialVal: payments
          .filter(
            (p) =>
              p.method !== "Online" &&
              p.method !== "AccountingSoftware" &&
              p.method !== "Chargeback" &&
              p.method !== "Refund" &&
              p.method !== "DirectDebit",
          )
          .filter((p) => p.amount.value < 0)
          .map((p) => ({ ...p, amount: absCurrency(p.amount) })),
        makeSubForm: async (paym) => {
          const ilForm = new Form({})
            .addField("date", [], () => dateBox({ initialVal: paym.date }))
            .addField("amount", [], () =>
              currencyBox(paym.amount, { constraint: "positive" }),
            )
            .addField("method", [], () => {
              const methods: paymentMethod[] = [
                "Cash",
                "Bancontact",
                "VISA",
                "Mastercard",
                "Wire",
                "Giftcard",
              ];
              return selectBox({
                values: methods,
                initial: paym.method,
                show: renderPaymentMethod,
              });
            })
            .build();
          return ilForm;
        },
        makeNew: () => ({
          date: LocalDate.now(),
          amount: currency(0),
          method: "Cash" as const,
        }),
        render: oneLineArrayRender((il) => il.amount.value === 0),
      }),
    )
    .addField(
      "total",
      ["invoicelines", "payments", "reimbursements", "creditnotes"],
      ({ invoicelines, payments, reimbursements, creditnotes }) => {
        const totalIls = invoicelines.reduce(
          (acc, il) => il.amount.add(acc),
          currency(0),
        );
        const totalOnlinePayments = (invoice.payments || [])
          .filter((p) => {
            const method = parsePaymentMethod(p.method);
            return (
              method === "Online" ||
              method === "AccountingSoftware" ||
              method === "Chargeback" ||
              method === "Refund" ||
              method === "DirectDebit"
            );
          })
          .reduce((acc, il) => il.amount.add(acc), currency(0));
        const totalPayments = (payments || []).reduce(
          (acc, il) => il.amount.add(acc),
          currency(0),
        );
        const totalReimbursements = (reimbursements || []).reduce(
          (acc, il) => il.amount.add(acc),
          currency(0),
        );
        const totalCreditnotes = creditnotes.reduce(
          (acc, il) => il.amount.add(acc),
          currency(0),
        );
        const toPay = totalIls
          .add(totalReimbursements)
          .subtract(totalOnlinePayments)
          .subtract(totalPayments)
          .subtract(totalCreditnotes);
        return constantFieldWithRender(
          0,
          <div>
            <label>
              {toPay.value >= 0
                ? "Nog te betalen"
                : "Terug te krijgen"}
            </label>
            {renderTableRow({
              col1: <div className="Style_input_like" style="opacity: 0"></div>,
              col2: (
                <div
                  className="Style_input_like"
                  style="filter: brightness(0.9);"
                >
                  {renderCurrency(toPay)}
                </div>
              ),
              col3: <div className="Style_input_like" style="opacity: 0"></div>,
            })}
          </div>,
        );
      },
    )
    .addField(
      "expectedpayments",
      ["invoicelines", "creditnotes"],
      async ({ invoicelines, creditnotes }) => {
        const initial = calcInitialExpectedPayments(
          expectedPaymentsMemoryS.get(),
          invoicelines,
          creditnotes,
        );

        const arrF = await makeArrayField({
          initialVal: initial,
          makeSubForm: async (paym) => {
            const ilForm = new Form({})
              .addField("date", [], () => dateBox({ initialVal: paym.date }))
              .addField("amount", [], () =>
                currencyBox(paym.amount, { constraint: "positive" }),
              )
              .addField("periodic_payment_method_", [], () => {
                const values = [
                  "none" as const,
                  "manual" as const,
                  "direct_debit" as const,
                ];
                return selectBox({
                  initial:
                    paym.periodic_payment_method === null
                      ? ("none" as const)
                      : paym.periodic_payment_method,
                  values: values,
                  show: (t) =>
                    t === "none"
                      ? "Eenmalig"
                      : t === "manual"
                      ? "Periodiek: Manueel"
                      : t === "direct_debit"
                      ? "Periodiek: Incasso"
                      : checkAllCasesHandled(t),
                });
              })
              .addField(
                "periodic_payment_method",
                ["periodic_payment_method_"],
                ({ periodic_payment_method_ }) =>
                  constantField(
                    periodic_payment_method_ === "none"
                      ? null
                      : periodic_payment_method_,
                  ),
              )
              .build();
            return ilForm;
          },
          makeNew: () => ({
            date: LocalDate.now(),
            amount: currency(0),
            periodic_payment_method:
              (invoice.expectedpayments || [])
                .orderBy((ep) => ep.date.toEpochDay(), "asc")
                .last()?.periodic_payment_method || null,
          }),
          render: oneLineArrayRender((il) => il.amount.value === 0),
        });

        arrF.s.observe((s) =>
          s.tag === "parsed" ? expectedPaymentsMemoryS.set(s.parsed) : null,
        );
        return arrF;
      },
    )
    .build({
      render: (fields) => {
        const adaptPaymentSchemeSection =
          payment_schemes.length > 1 ? (
            renderAdaptPaymentSchemeSection(invoice.id, payment_schemes)
          ) : (
            <span></span>
          );

        return (
          <div>
            <section
              className={style.card + style.card_accent}
              style="width: 100%;"
            >
              <label for="">{"Factuurlijnen"}</label>
              {fields.invoicelines()}

              <div style="padding-bottom: 64px;"></div>

              <label for="">{"Creditnota's"}</label>
              {renderTableHeader([
                "Factuurnummer",
                "Bedrag",
                "BTW%",
              ])}
              {(invoice.creditnotes || [])
                .filter((cn) => cn.accountingsoftwareidentifier !== null)
                .map((cn) => (
                  <div style="display: flex">
                    <div style="flex: 1">
                      <div>{ddMMMMyyyy(cn.date)}</div>
                      <div>{renderCurrency(cn.amount)}</div>
                      <div>{cn.externalid}</div>
                    </div>
                    <button style="visibility: hidden"></button>
                    <div style="flex: 0 1 auto"></div>
                  </div>
                ))}
              {fields.creditnotes()}

              <div style="padding-bottom: 64px;"></div>

              <label for="">{"Betalingen"}</label>
              {renderTableHeader([
                "Datum",
                "Bedrag",
                "Betaalmiddel",
              ])}
              {payments
                .filter(
                  (p) =>
                    p.method === "Online" ||
                    p.method === "AccountingSoftware" ||
                    p.method === "Chargeback" ||
                    p.method === "Refund" ||
                    p.method === "DirectDebit",
                )
                .filter((p) => p.amount.value > 0)
                .map(renderPaymentReadonly)}
              {fields.payments()}

              <div style="padding-bottom: 64px;"></div>

              <label for="">{"Terugbetalingen"}</label>
              {renderTableHeader([
                "Datum",
                "Bedrag",
                "Betaalmiddel",
              ])}
              {payments
                .filter(
                  (p) =>
                    p.method === "Online" ||
                    p.method === "AccountingSoftware" ||
                    p.method === "Chargeback" ||
                    p.method === "Refund" ||
                    p.method === "DirectDebit",
                )
                .filter((p) => p.amount.value < 0)
                .map(renderPaymentReadonly)}
              {fields.reimbursements()}

              <div style="padding-bottom: 64px;"></div>

              {fields.total()}
            </section>

            <section
              className={style.card + style.card_accent}
              style="width: 100%;"
            >
              <article style="display: flex;">
                <label for="" style="flex:1">
                  {"Verwachte betalingen"}
                </label>
                {adaptPaymentSchemeSection}
              </article>
              {fields.expectedpayments()}
            </section>
          </div>
        );
      },
    });

  return (
    <div>
      {form.render()}
      <div style="text-align: right">
        {renderCancelButton({
          label: "Annuleer",
          onclick: () => {
            document.location = routes.invoicedetailsRoute.link({
              invoiceid: invoice.id,
              edit: false,
            });
          },
        })}
        {renderSaveButton({
          formS: form.s,
          save: (values) => {
            return rpc(routes.saveInvoiceFullSpec, undefined, {
              id: values.id,
              invoicelines: values.invoicelines,
              creditnotes: values.creditnotes,
              payments: values.payments
                .filter((paym) => paym.amount.value !== 0)
                .concat(
                  values.reimbursements
                    .filter((reim) => reim.amount.value !== 0)
                    .map((r) => ({ ...r, amount: negateCurrency(r.amount) })),
                ),
              expectedpayments: values.expectedpayments.filter(
                (ep) => ep.amount.value !== 0,
              ),
            });
          },
          reload: () => {
            document.location = routes.invoicedetailsRoute.link({
              invoiceid: invoice.id,
              edit: false,
            });
          },
          timeout: 600,
        })}
      </div>
    </div>
  );
}

function calcInitialExpectedPayments(
  currEps: {
    date: LocalDate;
    amount: currency;
    periodic_payment_method: periodic_payment_method | null;
  }[],
  invoicelines: { description: string; amount: currency; vat: number }[],
  creditnotes: { amount: currency }[],
): {
  date: LocalDate;
  amount: currency;
  periodic_payment_method: periodic_payment_method | null;
}[] {
  const ilsTotal = invoicelines
    .reduce((acc, il) => acc.add(il.amount), currency(0))
    .subtract(creditnotes.reduce((acc, il) => acc.add(il.amount), currency(0)));
  if (ilsTotal.value < 0) {
    return currEps;
  }

  const epsTotal = currEps.reduce((acc, il) => acc.add(il.amount), currency(0));
  if (ilsTotal.value !== epsTotal.value) {
    if (currEps.length === 0) {
      // No ep => make a new one with full amount
      return [
        {
          amount: ilsTotal,
          date: LocalDate.now(),
          periodic_payment_method: null,
        },
      ];
    } else if (currEps.length === 1) {
      // One ep => Set full amount
      return [{ ...currEps[0], amount: ilsTotal }];
    } else if (
      currEps.length === 2 &&
      currEps.every((ep) => ep.periodic_payment_method === null)
    ) {
      // Two (non-periodic) eps but second would be negative -> one EP
      if (ilsTotal.subtract(currEps[0].amount)) {
        // One ep => Set full amount
        return [{ ...currEps[0], amount: ilsTotal }];
      } else {
        // Two (non-periodic) eps => Add everything to second ep
        return [
          currEps[0],
          { ...currEps[1], amount: ilsTotal.subtract(currEps[0].amount) },
        ];
      }
    } else if (
      currEps.some((currEp) => currEp.periodic_payment_method !== null)
    ) {
      // Periodic eps => add everything to periodic eps, divided
      const nonPeriodicEps = currEps.filter(
        (currEp) => currEp.periodic_payment_method === null,
      );
      const periodicEps = currEps.filter(
        (currEp) => currEp.periodic_payment_method !== null,
      );
      const amountToDivideOverPeriodicEps = ilsTotal.subtract(
        nonPeriodicEps.reduce((acc, ep) => acc.add(ep.amount), currency(0)),
      );
      return nonPeriodicEps.concat(
        periodicEps.map((ep) => ({
          ...ep,
          amount: amountToDivideOverPeriodicEps.divide(periodicEps.length),
        })),
      );
    }
  }

  return currEps;
}

function renderTableHeader(opts: [string, string, string]) {
  return renderTableRow({
    col1: (
      <div>
        <label style="font-weight: 200;">{opts[0]}</label>
      </div>
    ),
    col2: (
      <div>
        <label style="font-weight: 200;">{opts[1]}</label>
      </div>
    ),
    col3: (
      <div>
        <label style="font-weight: 200;">{opts[2]}</label>
      </div>
    ),
  });
}

function renderTableRow(opts: {
  col1: HTMLElement;
  col2: HTMLElement;
  col3: HTMLElement;
}) {
  return (
    <div style="display: flex;">
      <div style="flex: 1 1 0%; display: grid; grid-template-columns: repeat(auto-fit, minmax(1px, 1fr)); gap: 0px 12px;">
        {opts.col1}
        {opts.col2}
        {opts.col3}
        <button style="min-width: 40px; max-width: 40px; grid-column: -1; opacity: 0;"></button>
      </div>
    </div>
  );
}

function renderAdaptPaymentSchemeSection(
  invoiceId: invoiceid,
  payment_schemes: (non_periodic_payment_scheme | periodic_payment_scheme)[],
) {
  const showS = new Source(false);
  const form = new Form({})
    .addField("payment_scheme", [], () =>
      selectBox({
        initial: payment_schemes[0],
        values: payment_schemes,
        show: (ps) => ps.label,
      }),
    )
    .build();
  return dyn(showS, (show) =>
    show ? (
      <div style="display: flex">
        {form.render()}
        <div style="margin-bottom: 16px; margin-left: 16px;">
          {renderSaveButton({
            formS: form.s,
            save: (values) =>
              rpc(routes.switchPaymentSchemeSpec, undefined, {
                newPaymentScheme: values.payment_scheme,
                invoiceid: invoiceId,
              }),
            text: {
              normal: "Pas toe",
              success: "Gelukt!",
            },
            reload: true,
          })}
        </div>
        <div style="margin-bottom: 16px; margin-left: 16px;">
          {renderCancelButton({ onclick: () => showS.set(false) })}
        </div>
      </div>
    ) : (
      <button onclick={() => showS.set(true)}>
        {"Pas betalingsschema in 1 keer toe"}
      </button>
    ),
  );
}

function renderPaymentReadonly(p: {
  amount: currency;
  date: LocalDate;
  method: paymentMethod;
}) {
  return (
    <div style="display: flex">
      <div style="flex: 1; display: grid; grid-template-columns: repeat(auto-fit, minmax(1px, 1fr)); gap: 0px 12px; ">
        <div className={style.input_like} style="filter: brightness(0.9)">
          {ddMMMMyyyy(p.date)}
        </div>
        <div className={style.input_like} style="filter: brightness(0.9)">
          {renderCurrency(p.amount)}
        </div>
        <div className={style.input_like} style="filter: brightness(0.9)">
          {renderPaymentMethod(p.method)}
        </div>
        <button style=" visibility: hidden; grid-column: -1; min-width: 40px; padding: 0px;"></button>
      </div>
    </div>
  );
}

export function invoicelinesField(invoice: {
  invoicelines: {
    amount: price_adapted_to_lg_and_student;
    description: string;
    vat: number;
    ordering: number;
  }[];
}): Promise<
  Field<
    {
      amount: price_adapted_to_lg_and_student;
      description: string;
      vat: number;
    }[]
  >
> {
  return makeArrayField({
    initialVal: (invoice.invoicelines || []).orderBy(
      [(il) => (il.amount.value > 0 ? 0 : 1), (il) => il.ordering],
      ["asc", "asc"],
    ),
    makeSubForm: async (il) => {
      const ilForm = new Form({})
        .addField("description", [], () =>
          textBox({
            initialVal: il.description,
            validations: [mandatoryValidation],
          }),
        )
        .addField(
          "amount",
          [],
          () => currencyBox(il.amount) as Promise<Field<typeof il.amount>>,
        )
        .addField("vat", [], () =>
          numberBox(il.vat, { constraint: "positive" }),
        )
        .build();
      return ilForm;
    },
    makeNew: () => ({
      description: "",
      amount: currency(0) as price_adapted_to_lg_and_student,
      vat: (invoice.invoicelines || [])[0]?.vat || 0,
    }),
    render: oneLineArrayRenderWithAlwaysAddAndDelete({
      headers: ["Factuurlijnen", "Bedrag", "BTW %"],
    }),
  });
}
